import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { chatUtil } from "../utility/ChatUtils";
import { useAuthContext } from "./AuthContext";
import useGraphql from "../Hooks/useGraphql";
import {
  GET_USER_CHAT_ROOMS_INSPECTOR,
  GET_USER_CHAT_ROOM_CONVERSATION_INSPECTOR,
} from "../pages/Messages/MessagesService";

const MessagesContext = createContext();
const { Provider } = MessagesContext;

/**
 * mode:
 *  - "normal"   → current behavior (sockets, chatUtil)
 *  - "inspector"→ super-admin inspector (GraphQL, read-only)
 *
 * inspectedUserId:
 *  - used only in inspector mode → whose chats we inspect
 */
const MessagesProvider = ({
  children,
  mode = "normal",
  inspectedUserId = null,
}) => {
  const auth = useAuthContext();
  const user = auth?.authState?.user || null;
  const club = user?.club;
  const clubId = club?._id ? String(club._id) : null;
  const isInspector = mode === "inspector";

  const { axiosGraphQL } = useGraphql();

  // Keep axiosGraphQL stable for callbacks (avoid infinite deps)
  const axiosGraphQLRef = useRef(axiosGraphQL);
  useEffect(() => {
    axiosGraphQLRef.current = axiosGraphQL;
  }, [axiosGraphQL]);

  const [latestUpdate, setLatestUpdate] = useState({});
  const [rooms, setRooms] = useState([]);
  const [chatClient, setChatClient] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // SETUP / CONNECT (only for normal mode)
  const setup = useCallback(async () => {
    if (isInspector) return; // no sockets in inspector mode
    try {
      await chatUtil.connect();
      setChatClient(chatUtil);
    } catch (err) {
      console.error("Chat connect failed:", err);
    }
  }, [isInspector]);

  const chatConnect = useCallback(() => {
    if (isInspector) return Promise.resolve();
    return setup();
  }, [setup, isInspector]);

  const onSelectRoom = useCallback((roomIdOrObject) => {
    setSelectedRoom(roomIdOrObject);
  }, []);

  // normal: via socket
  // inspector: via GraphQL inspector endpoints
  const getRooms = useCallback(async () => {
    // INSPECTOR MODE: use GraphQL inspector endpoint
    if (isInspector) {
      if (!inspectedUserId) return;
      try {
        const res = await axiosGraphQLRef.current({
          query: GET_USER_CHAT_ROOMS_INSPECTOR,
          variables: { userId: inspectedUserId, club: clubId },
        });

        const payload = res?.data?.data?.getUserChatRoomsInspector;
        const parsed =
          typeof payload === "string" ? JSON.parse(payload) : payload || [];

        setRooms(parsed);
      } catch (err) {
        console.error("Inspector getRooms error:", err);
      }
      return;
    }

    // NORMAL MODE: via socket
    if (!chatClient) return;
    chatClient.getRooms(setRooms);
  }, [isInspector, inspectedUserId, chatClient]);

  // normal: subscribe with sockets
  // inspector: just refresh rooms (no live subscription)
  const getLatestMessage = useCallback(() => {
    if (isInspector) {
      // In inspector mode we don't subscribe; just refresh rooms when needed
      getRooms().then(() => {
        setLatestUpdate({ ts: Date.now() });
      });
      return;
    }

    if (!chatClient || !clubId) return;

    chatClient.subscribeForMessages((conversation) => {
      setLatestUpdate(conversation);
      getRooms();
    }, clubId);
  }, [isInspector, chatClient, clubId, getRooms]);

  // normal: socket getConversation
  // inspector: GraphQL inspector endpoint (read-only)
  const getConversation = useCallback(
    (params, callback) => {
      const { roomId, skip = 0, limit = 20, seenBy = false } = params || {};

      if (!roomId) {
        callback && callback({ message: [] });
        return;
      }

      if (isInspector) {
        if (!inspectedUserId) {
          callback && callback({ message: [] });
          return;
        }

        axiosGraphQLRef
          .current({
            query: GET_USER_CHAT_ROOM_CONVERSATION_INSPECTOR,
            variables: { userId: inspectedUserId, roomId, skip, limit },
          })
          .then((res) => {
            const payload =
              res?.data?.data?.getUserChatRoomConversationInspector;
            const parsed =
              typeof payload === "string"
                ? JSON.parse(payload)
                : payload || { message: [] };
            callback && callback(parsed);
          })
          .catch((err) => {
            console.error("Inspector getConversation error:", err);
            callback && callback({ message: [] });
          });

        return;
      }

      // NORMAL MODE → socket
      if (!chatClient) return;
      return chatClient.getConversation(
        { roomId, skip, limit, seenBy },
        callback
      );
    },
    [isInspector, inspectedUserId, chatClient]
  );

  // Mutating actions
  // disabled in inspector (read-only)
  const createRoom = useCallback(
    (roomName, clubIdFromCaller) => {
      if (isInspector) {
        console.warn("Inspector mode: createRoom is disabled");
        return Promise.reject(new Error("Inspector is read-only"));
      }
      if (!chatClient) return;
      // your GraphQL CreateRoom resolver handles club internally;
      // we just pass params as you had before
      return chatClient.createRoom(roomName, clubIdFromCaller);
    },
    [chatClient, isInspector]
  );

  const addUserToRoom = useCallback(
    (userId, roomId, isPrivate = false) => {
      if (isInspector) {
        console.warn("Inspector mode: addUserToRoom is disabled");
        return Promise.reject(new Error("Inspector is read-only"));
      }
      if (!chatClient) return;
      return chatClient.addUserToRoom(userId, roomId, isPrivate);
    },
    [chatClient, isInspector]
  );

  const removeUserFromRoom = useCallback(
    (userId, roomId) => {
      if (isInspector) {
        console.warn("Inspector mode: removeUserFromRoom is disabled");
        return Promise.reject(new Error("Inspector is read-only"));
      }
      if (!chatClient) return;
      return chatClient.removeUserFromRoom(userId, roomId);
    },
    [chatClient, isInspector]
  );

  // use local rooms cache in both modes
  const getRoomById = useCallback(
    (roomId) => {
      if (!rooms || !roomId) return Promise.resolve(null);
      const found = rooms.find((r) => r.room?._id === roomId);
      return Promise.resolve(found || null);
    },
    [rooms]
  );

  // AUTO-FETCH FOR INSPECTOR
  useEffect(() => {
    if (!isInspector) return;
    if (!inspectedUserId) return;
    getRooms();
  }, [isInspector, inspectedUserId, getRooms]);

  useEffect(() => {
    // whenever we switch mode or inspected user, clear state
    setRooms([]);
    setSelectedRoom(null);
    setLatestUpdate({});

    // optional: in normal mode, ensure chat is connected
    if (!isInspector) {
      chatConnect();
    } else {
      // in inspector mode, fetch immediately if we have a target user
      if (inspectedUserId) {
        getRooms();
      }
    }
  }, [mode, inspectedUserId, isInspector, chatConnect, getRooms]);

  const value = useMemo(
    () => ({
      mode,
      isInspector,
      chatClient,
      selectedRoom,
      onSelectRoom,
      createRoom,
      addUserToRoom,
      removeUserFromRoom,
      getLatestMessage,
      getRoomById,
      latestUpdate,
      chatConnect,
      getRooms,
      rooms,
      getConversation,
    }),
    [
      mode,
      isInspector,
      chatClient,
      selectedRoom,
      onSelectRoom,
      createRoom,
      addUserToRoom,
      removeUserFromRoom,
      getLatestMessage,
      getRoomById,
      latestUpdate,
      chatConnect,
      getRooms,
      rooms,
      getConversation,
    ]
  );

  return <Provider value={value}>{children}</Provider>;
};

export const useMessages = () => useContext(MessagesContext);

export default MessagesProvider;
